import { useState } from 'react';
import getConfig from 'next/config';

import { useUserId } from 'shared/contexts/UserIdProvider';
import { LoginModal } from '@/components/bookingLoginBoundary/components/loginModal/LoginModal';
import type { HotelOffer, RoomWithPeople } from '@/interfaces';
import { trackBooking, trackBookingLoginRequired, trackLogin, trackLoginClose } from '@/utils/tracking/finnAnalytics';
import { offerToBookingPageQueryParams } from '@/utils/url/bookingPageQueryParams';
import { offerToOutPageQueryParams } from '@/utils/url/outPageQueryParams';

const { publicRuntimeConfig } = getConfig();

export interface Props {
    offer: HotelOffer;
    rooms: readonly RoomWithPeople[];
    trackingPage: string;
    buttonClass: string;
    buttonText: string;
}

export const BookingLoginBoundary = ({ offer, rooms, trackingPage, buttonClass, buttonText }: Props) => {
    const { userId } = useUserId();
    const [modalOpen, setModalOpen] = useState(false);

    const integratedBookingUrl =
        offer.integratedBooking && `${publicRuntimeConfig.basePath}/bestill/${offerToBookingPageQueryParams(offer, rooms)}`;
    const externalBookingUrl = `${publicRuntimeConfig.basePath}/ut/?${offerToOutPageQueryParams(offer, trackingPage)}`;

    const onOpenModal = () => {
        trackBookingLoginRequired(offer);
        setModalOpen(true);
    };
    const onLoginClick = () => trackLogin(offer.hotelId);
    const onCloseModal = () => {
        trackLoginClose(offer.hotelId);
        setModalOpen(false);
    };

    return offer.loginRequired && !userId ? (
        <>
            <button type="button" className={buttonClass} style={{ zIndex: 2 }} onClick={onOpenModal}>
                {buttonText}
            </button>
            <LoginModal
                redirectPath={offer.integratedBooking ? integratedBookingUrl : externalBookingUrl}
                open={modalOpen}
                onLoginClick={onLoginClick}
                onCloseModal={onCloseModal}
            />
        </>
    ) : (
        <a
            className={buttonClass}
            data-testid="order-link"
            style={{ zIndex: 2 }}
            href={offer.integratedBooking ? integratedBookingUrl : externalBookingUrl}
            target={offer.integratedBooking ? null : '_blank'}
            onClick={() => trackBooking(offer)}
            rel="noreferrer">
            {buttonText}
        </a>
    );
};
